import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import './Admin.css';

const CATEGORIES = ['⚙️ 제조업 도급/ 아웃소싱', '🎉 행사 대행 단기 인력', '💰 공통 (비용 및 계약)'];

const emptyForm = { question: '', answer: '', category: CATEGORIES[0], sortOrder: 0 };

export default function AdminFaqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [filter, setFilter] = useState('전체');

  useEffect(() => { loadFaqs(); }, []);

  async function loadFaqs() {
    try {
      setLoading(true);
      const data = await adminApi.get('/faqs');
      setFaqs(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(faq) {
    if (!window.confirm(`"${faq.question}" 항목을 삭제하시겠습니까?`)) return;
    try {
      await adminApi.delete(`/faqs/${faq.id}`);
      loadFaqs();
    } catch (err) {
      alert(err.message);
    }
  }

  const filteredFaqs = faqs.filter(f => filter === '전체' || f.category === filter);

  if (editingFaq !== null) {
    return (
      <AdminLayout>
        <FaqForm
          initial={editingFaq}
          onCancel={() => setEditingFaq(null)}
          onSaved={() => { setEditingFaq(null); loadFaqs(); }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>FAQ 관리</h1>
          <p className="text-muted">자주 묻는 질문을 카테고리별로 관리합니다.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditingFaq({})}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          FAQ 등록
        </button>
      </div>

      <div className="admin-filter-row">
        {['전체', ...CATEGORIES].map(f => (
          <button
            key={f}
            className={`type-filter__btn ${filter === f ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}

      {!loading && !error && (
        filteredFaqs.length === 0 ? (
          <div className="state-message">등록된 FAQ가 없습니다.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 220 }}>카테고리</th>
                  <th>질문</th>
                  <th style={{ width: 60, textAlign: 'center' }}>순서</th>
                  <th style={{ width: 140 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredFaqs.map(faq => (
                  <tr key={faq.id}>
                    <td><span className="badge badge-navy" style={{ whiteSpace: 'nowrap' }}>{faq.category}</span></td>
                    <td className="admin-table__title">{faq.question}</td>
                    <td style={{ textAlign: 'center' }}>{faq.sort_order}</td>
                    <td className="admin-table__actions">
                      <button className="admin-action-btn" onClick={() => setEditingFaq(faq)}>수정</button>
                      <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(faq)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AdminLayout>
  );
}

function FaqForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState(() => isEdit ? {
    question: initial.question || '',
    answer: initial.answer || '',
    category: initial.category || CATEGORIES[0],
    sortOrder: initial.sort_order || 0,
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question || !form.answer) {
      setError('질문과 답변은 필수입니다.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await adminApi.put(`/faqs/${initial.id}`, form);
      } else {
        await adminApi.post('/faqs', form);
      }
      onSaved();
    } catch (err) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button className="back-btn" onClick={onCancel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        목록으로
      </button>
      <div className="admin-form-card">
        <h2>{isEdit ? 'FAQ 수정' : 'FAQ 등록'}</h2>
        <form onSubmit={handleSubmit}>
          {error && <div className="admin-alert admin-alert--error">{error}</div>}
          <div className="form-row-2">
            <div className="form-group">
              <label>카테고리 <span className="required">*</span></label>
              <select name="category" value={form.category} onChange={handleChange}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>표시 순서</label>
              <input type="number" name="sortOrder" value={form.sortOrder} onChange={handleChange} min={0} />
            </div>
          </div>
          <div className="form-group">
            <label>질문 <span className="required">*</span></label>
            <input name="question" value={form.question} onChange={handleChange} placeholder="자주 묻는 질문을 입력하세요" />
          </div>
          <div className="form-group">
            <label>답변 <span className="required">*</span></label>
            <textarea name="answer" value={form.answer} onChange={handleChange} rows={6} placeholder="질문에 대한 답변을 입력하세요" />
          </div>
          <div className="admin-form-actions">
            <button type="button" className="btn btn-outline-navy" onClick={onCancel}>취소</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '저장 중...' : isEdit ? '수정 완료' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
